import { useState, useEffect, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { X, UserPlus, Loader2, Star } from 'lucide-react';
import {
  useCreateTeam,
  useUpdateTeam,
  useTeamMembers,
  useAddTeamMember,
  useRemoveTeamMember,
  useUpdateTeamMemberRole,
} from '@/features/rocks/hooks/useTeams';
import { useEmployees } from '@/features/teams/api/employees';
import { MEMBER_ROLES, type EmployeeRow } from '../types';
import type { Team } from '../types';

interface PendingMember {
  userId: string; // PG UUID
  role: string;
}

interface TeamPanelProps {
  /** Team to edit, or null for create mode. */
  team: Team | null;
  /** Called when the panel should close (after save or cancel). */
  onClose: () => void;
}

function getUserDisplayName(emp: EmployeeRow): string {
  const name = [emp.firstname, emp.lastname].filter(Boolean).join(' ');
  return name || emp.email || '—';
}

function getInitials(emp: EmployeeRow): string {
  const first = emp.firstname?.[0] ?? '';
  const last = emp.lastname?.[0] ?? '';
  return (first + last).toUpperCase() || (emp.email?.[0] ?? '?').toUpperCase();
}

/**
 * Side-panel content for creating or editing a Team. Designed to be rendered
 * inside `<SlidePanel>` (Rocks-style mainframe). Replaces the legacy
 * `TeamModal` for the new layout.
 *
 * NOTE: team_members.user_id is a PG UUID (post 0198 migration), so the
 * userMap is indexed by `pg_id` and addMember/removeMember calls pass the
 * caller's pg_id, never the Mongo _id.
 */
export function TeamPanel({ team, onClose }: TeamPanelProps) {
  const isEditing = !!team;
  const [name, setName] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(''); // PG UUID
  const [memberRole, setMemberRole] = useState('member');
  const [pendingMembers, setPendingMembers] = useState<PendingMember[]>([]);

  const createTeam = useCreateTeam();
  const updateTeam = useUpdateTeam();
  const addMember = useAddTeamMember();
  const removeMember = useRemoveTeamMember();
  const updateMemberRole = useUpdateTeamMemberRole();
  const isPending = createTeam.isPending || updateTeam.isPending;

  const { data: existingMembers = [], isLoading: membersLoading } = useTeamMembers(
    isEditing ? team!.id : '',
  );

  // Only ATEK employees/contractors/service-accounts can be team members.
  // /v4/employees already excludes customers, disabled, and former employees.
  const { data: employeesData } = useEmployees();
  const employees = employeesData?.employees ?? [];

  // team_members.user_id is a PG UUID (post 0198) — index by employee.id.
  const userMap = useMemo(
    () => new Map<string, EmployeeRow>(employees.map((e) => [e.id, e])),
    [employees],
  );

  const addedUserIds = useMemo(() => {
    const existing = isEditing ? existingMembers.map((m) => m.userId) : [];
    const pending = pendingMembers.map((m) => m.userId);
    return new Set([...existing, ...pending]);
  }, [isEditing, existingMembers, pendingMembers]);
  const availableUsers = employees.filter((e) => !addedUserIds.has(e.id));

  // Reset form whenever the target team changes (create vs edit, or different team).
  useEffect(() => {
    setName(team?.name ?? '');
    setSelectedUserId('');
    setMemberRole('member');
    setPendingMembers([]);
  }, [team?.id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    if (isEditing) {
      await updateTeam.mutateAsync({ id: team!.id, data: { name: name.trim() } });
    } else {
      const newTeam = await createTeam.mutateAsync({
        name: name.trim(),
        type: 'custom',
        is_private: false,
      });
      if (newTeam?.id && pendingMembers.length > 0) {
        for (const m of pendingMembers) {
          await addMember.mutateAsync({ teamId: newTeam.id, userId: m.userId, role: m.role });
        }
      }
    }
    onClose();
  };

  const handleAddMember = async () => {
    if (!selectedUserId) return;
    if (isEditing) {
      await addMember.mutateAsync({ teamId: team!.id, userId: selectedUserId, role: memberRole });
    } else {
      setPendingMembers((prev) => [...prev, { userId: selectedUserId, role: memberRole }]);
    }
    setSelectedUserId('');
    setMemberRole('member');
  };

  const handleRemoveMember = async (userId: string) => {
    if (isEditing) {
      await removeMember.mutateAsync({ teamId: team!.id, userId });
    } else {
      setPendingMembers((prev) => prev.filter((m) => m.userId !== userId));
    }
  };

  const handleToggleLeader = async (userId: string, currentRole: string) => {
    const next = currentRole === 'leader' ? 'member' : 'leader';
    if (isEditing) {
      await updateMemberRole.mutateAsync({ teamId: team!.id, userId, role: next });
    } else {
      setPendingMembers((prev) => prev.map((m) => m.userId === userId ? { ...m, role: next } : m));
    }
  };

  const displayMembers = isEditing
    ? existingMembers.map((m) => ({ userId: m.userId, role: m.role }))
    : pendingMembers;

  return (
    <form onSubmit={handleSubmit} className="flex flex-col h-full">
      {/* Sticky header */}
      <div className="sticky top-0 z-10 bg-background border-b px-4 py-3 flex items-center justify-between">
        <h2 className="text-base font-semibold">{isEditing ? team!.name : 'New team'}</h2>
        <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        <div className="space-y-2">
          <Label htmlFor="team-name">Name <span className="text-destructive">*</span></Label>
          <Input
            id="team-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Leadership"
            required
            autoFocus
          />
        </div>

        <div className="space-y-3 border-t pt-4">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">Members</Label>
            <span className="text-xs text-muted-foreground">
              {displayMembers.length} member{displayMembers.length !== 1 ? 's' : ''}
            </span>
          </div>

          {membersLoading && isEditing ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-10 rounded bg-muted animate-pulse" />
              ))}
            </div>
          ) : displayMembers.length === 0 ? (
            <p className="text-sm text-muted-foreground py-3">No members yet.</p>
          ) : (
            <div className="space-y-0.5">
              {displayMembers.map((m) => {
                const user = userMap.get(m.userId);
                return (
                  <div key={m.userId} className="flex items-center gap-3 py-2 px-2 rounded hover:bg-muted/50">
                    <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary flex-shrink-0">
                      {user ? getInitials(user) : '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {user ? getUserDisplayName(user) : m.userId}
                      </p>
                      {user?.email && (
                        <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant={m.role === 'leader' ? 'default' : 'ghost'}
                      size="sm"
                      className={cn('h-7 px-2 text-xs flex-shrink-0', m.role === 'leader' && 'bg-amber-500 hover:bg-amber-600 text-white')}
                      onClick={() => handleToggleLeader(m.userId, m.role)}
                      disabled={updateMemberRole.isPending}
                      title={m.role === 'leader' ? 'Demote to member' : 'Promote to leader'}
                    >
                      <Star className={cn('h-3 w-3 mr-1', m.role === 'leader' && 'fill-current')} />
                      {m.role === 'leader' ? 'Leader' : 'Member'}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 flex-shrink-0 text-muted-foreground hover:text-destructive"
                      onClick={() => handleRemoveMember(m.userId)}
                      disabled={removeMember.isPending}
                      title="Remove from team"
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Add member */}
          <div className="flex items-end gap-2 border rounded-md p-3 bg-muted/30">
            <div className="flex-1 space-y-1">
              <Label className="text-xs text-muted-foreground">Add member</Label>
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue placeholder="Select user…" />
                </SelectTrigger>
                <SelectContent>
                  {availableUsers.length === 0 ? (
                    <div className="p-2 text-sm text-muted-foreground text-center">
                      All users already added
                    </div>
                  ) : (
                    availableUsers.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {getUserDisplayName(u)}
                        {u.email && (
                          <span className="ml-1 text-muted-foreground text-xs">({u.email})</span>
                        )}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="w-28 space-y-1">
              <Label className="text-xs text-muted-foreground">Role</Label>
              <Select value={memberRole} onValueChange={setMemberRole}>
                <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MEMBER_ROLES.map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8"
              onClick={handleAddMember}
              disabled={!selectedUserId || addMember.isPending}
            >
              <UserPlus className="h-3.5 w-3.5 mr-1" /> Add
            </Button>
          </div>
        </div>
      </div>

      {/* Sticky footer */}
      <div className="sticky bottom-0 bg-background border-t px-4 py-3 flex items-center justify-end gap-2">
        <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button type="submit" disabled={isPending || !name.trim()}>
          {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {isEditing ? 'Save changes' : 'Create team'}
        </Button>
      </div>
    </form>
  );
}

export default TeamPanel;
